import React, { useEffect } from 'react';
import DOMPurify from 'dompurify';
import { RichTextDisplay } from '@/components/ui/rich-text-display';
import { Link, useLocation } from 'react-router-dom';
import { usePageContent } from '@/hooks/usePageContent';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { AlertCircle, FileText, Settings, ArrowRight } from 'lucide-react';
import { useSiteConfig } from '@/lib/useSiteConfig';
import { SEO } from '@/components/SEO';
import NotFound from '@/pages/NotFound';

interface PageRendererProps {
  isAdmin?: boolean;
  customSlug?: string;
}

// Default content for pages that haven't been created yet
const getDefaultContent = (slug: string, organizationName: string) => {
  const defaults: Record<string, { title: string; description: string; message: string }> = {
    about: {
      title: 'About Us',
      description: `Learn more about ${organizationName}`,
      message: 'This page content can be customized through the admin dashboard. Add information about your organization, mission, vision, and team.'
    },
    programs: {
      title: 'Our Programs',
      description: 'Discover the programs and services we offer',
      message: 'This page content can be customized through the admin dashboard. Add details about your programs, services, and initiatives.'
    },
    membership: {
      title: 'Membership',
      description: 'Join our community',
      message: 'This page content can be customized through the admin dashboard. Add membership benefits, tiers, and registration information.'
    },
    donate: {
      title: 'Support Us',
      description: 'Help us make a difference',
      message: 'This page content can be customized through the admin dashboard. Add donation information and impact stories.'
    },
    privacy: {
      title: 'Privacy Policy',
      description: 'How we handle your information',
      message: 'This page content can be customized through the admin dashboard. Add your privacy policy details.'
    },
    accessibility: {
      title: 'Accessibility Statement',
      description: 'Our commitment to accessibility',
      message: 'This page content can be customized through the admin dashboard. Add your accessibility commitment and features.'
    }
  };

  return defaults[slug] || {
    title: slug.charAt(0).toUpperCase() + slug.slice(1).replace(/-/g, ' '),
    description: '',
    message: 'This page content can be customized through the admin dashboard.'
  };
};

const sanitizeHtml = (content: string) =>
  DOMPurify.sanitize(content, {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'blockquote', 'a', 'img', 'figure', 'figcaption',
      'div', 'span', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'class', 'style', 'width', 'height'],
    ALLOW_DATA_ATTR: false,
  });

export const PageRenderer: React.FC<PageRendererProps> = ({ 
  isAdmin = false, 
  customSlug 
}) => {
  const config = useSiteConfig();
  const location = useLocation();
  const { page, isLoading, error } = usePageContent({ isAdmin, customSlug });
  const slug = customSlug || window.location.pathname.split('/').pop() || 'home';

  // Handle hash fragment scrolling after page loads
  useEffect(() => {
    if (!isLoading && page && location.hash) {
      // Small delay to ensure page is rendered
      setTimeout(() => {
        const element = document.querySelector(location.hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [isLoading, page, location.hash]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-warm">
        <div className="max-w-4xl mx-auto px-4 py-16">
          <Skeleton className="h-12 w-3/4 mb-4" />
          <Skeleton className="h-6 w-1/2 mb-8" />
          <div className="space-y-3">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-4 w-full" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-warm">
        <div className="max-w-4xl mx-auto px-4 py-16">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {error.message || 'Failed to load page content'}
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  // For non-admin users: only show published pages, return null for missing/unpublished pages to trigger 404
  if (!page) {
    // Show placeholder content only in admin mode
    if (isAdmin) {
      const defaultContent = getDefaultContent(slug, config.organizationName);
      
      return (
        <div className="min-h-screen bg-gradient-warm">
          {/* Hero Section */}
          <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary/10 via-heritage/5 to-transparent">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
                {defaultContent.title}
              </h1>
              {defaultContent.description && (
                <p className="text-xl text-muted-foreground">
                  {defaultContent.description}
                </p>
              )}
            </div>
          </section>

          {/* Setup Message */}
          <section className="py-16 px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl mx-auto">
              <Card className="shadow-warm">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-heritage/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <FileText className="w-8 h-8 text-heritage" />
                  </div>
                  <h2 className="text-2xl font-bold text-foreground mb-4">
                    Page Content Not Yet Created
                  </h2>
                  <p className="text-muted-foreground mb-6">
                    {defaultContent.message}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button variant="heritage" asChild>
                      <Link to="/admin/dashboard/pages">
                        <Settings className="w-4 h-4 mr-2" />
                        Go to Admin Dashboard
                      </Link>
                    </Button>
                    <Button variant="outline" asChild>
                      <Link to="/">
                        Return Home
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Link>
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground mt-6">
                    Navigate to Admin → Pages to create and edit this page content.
                  </p>
                </CardContent>
              </Card>
            </div>
          </section>
        </div>
      );
    }
    // For public users, show 404 for missing/unpublished pages
    return <NotFound />;
  }

  // Hide unpublished pages from public view - show 404
  // The service layer already filters unpublished pages, but double-check here
  if (!isAdmin && page && page.is_published === false) {
    return <NotFound />;
  }

  // Render CMS content
  const renderContent = () => {
    if (!page.content.blocks || page.content.blocks.length === 0) {
      return (
        <p className="text-muted-foreground italic">
          No content has been added to this page yet. Edit this page in the admin dashboard to add content.
        </p>
      );
    }

    return page.content.blocks.map((block, index) => {
      // Handle TinyMCE rich text HTML format (blocks with data.html)
      if (block.data?.html) {
        return (
          <RichTextDisplay
            key={index}
            content={block.data.html}
            className="prose prose-lg max-w-none"
          />
        );
      }
      
      // Handle Editor.js format blocks (with block.type)
      switch (block.type) {
        case 'header': {
          const level = block.data.level || 2;
          const className = level === 1
            ? 'text-3xl font-bold my-6'
            : level === 2
            ? 'text-2xl font-bold my-5'
            : 'text-xl font-semibold my-4';
          const HeaderTag = `h${level}` as keyof JSX.IntrinsicElements;
          return <HeaderTag key={index} className={className}>{block.data.text}</HeaderTag>;
        }
        
        case 'html':
          return (
            <RichTextDisplay
              key={index}
              content={block.data?.html || ''}
              className="prose prose-lg max-w-none"
            />
          );

        case 'paragraph': {
          // TinyMCE may store HTML in either data.html or data.text; Editor.js stores plain text/HTML in data.text
          const rawHtml = block.data?.html ?? block.data?.text ?? '';
          const hasRichHtml = /<(h[1-6]|p|ul|ol|li|blockquote|figure|img|table|strong|b|em|i|span|div)[\s>]/i.test(rawHtml);
          if (hasRichHtml) {
            return (
              <RichTextDisplay
                key={index}
                content={rawHtml}
                className="prose prose-lg max-w-none"
              />
            );
          }
          return (
            <div
              key={index}
              className="mb-4 text-foreground leading-relaxed"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(rawHtml) }}
            />
          );
        }
        
        case 'list': {
          const ListTag = block.data.style === 'ordered' ? 'ol' : 'ul';
          const listClass = block.data.style === 'ordered' ? 'list-decimal' : 'list-disc';
          return (
            <ListTag key={index} className={`${listClass} pl-6 mb-4 space-y-1`}>
              {block.data.items?.map((item, i) => (
                <li
                  key={i}
                  className="text-foreground"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(item || '') }}
                />
              ))}
            </ListTag>
          );
        }
        
        case 'image':
          return (
            <figure key={index} className="my-8">
              <img
                src={block.data.file?.url}
                alt={block.data.caption || ''}
                className="w-full h-auto rounded-lg shadow-warm"
              />
              {block.data.caption && (
                <figcaption className="text-sm text-muted-foreground text-center mt-3">
                  {block.data.caption}
                </figcaption>
              )}
            </figure>
          );
        
        case 'delimiter':
          return <hr key={index} className="my-8 border-t border-border" />;
        
        case 'quote':
          return (
            <blockquote key={index} className="border-l-4 border-heritage pl-6 py-2 my-6 italic bg-heritage/5 rounded-r-lg">
              <p className="text-foreground">{block.data.text}</p>
              {block.data.caption && (
                <footer className="text-sm text-muted-foreground mt-2 not-italic">
                  — {block.data.caption}
                </footer>
              )}
            </blockquote>
          );
        
        default:
          // Fallback: if block has text data, render as paragraph
          if (block.data?.text) {
            return (
              <div
                key={index}
                className="mb-4 text-foreground leading-relaxed"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(block.data.text) }}
              />
            );
          }
          return null;
      }
    });
  };

  return (
    <div className="min-h-screen bg-gradient-warm">
      <SEO
        title={page.title}
        description={page.description || page.seo_description || `Learn more about ${page.title}`}
        keywords={Array.isArray(page.seo_keywords) ? page.seo_keywords : (page.seo_keywords ? [page.seo_keywords] : [])}
        canonical={`${typeof window !== 'undefined' ? window.location.origin : ''}/${page.slug}`}
        noindex={!page.is_published}
      />
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary/10 via-heritage/5 to-transparent">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
            {page.title}
          </h1>
          {page.description && (
            <p className="text-xl text-muted-foreground">
              {page.description}
            </p>
          )}
        </div>
      </section>

      {/* Page Content */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <article className="max-w-4xl mx-auto">
          {renderContent()}
        </article>
      </section>
    </div>
  );
};
